// /about/team — E-E-A-T page with Person schema for 6 licensed attorneys.
// Establishes "Expertise, Experience, Authoritativeness, Trustworthiness" signals
// that Google and AI Overviews use to rank legal/YMYL content.
import { createFileRoute, Link } from "@tanstack/react-router";

const LICENSE_TO_SLUG: Record<string, string> = {
  "942/2554": "anutaree",
  "1112/2551": "jirapan",
  "4759/2565": "jirasak",
  "1114/2558": "patipan",
  "3041/2565": "warawut",
  "931/2551": "wiwat",
};
import { credentials } from "@/data/credentials";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { SITE_CONTENT_REVIEWED } from "@/lib/site-content-meta";
import { consolidateGraph } from "@/lib/schema-graph";
import { NYC_POSTAL_ADDRESS } from "@/lib/aeo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/about/team`;

type Attorney = {
  nameTh: string;
  nameEn: string;
  license: string;
  licenseYear: string;
  image: string;
  role: string;
  yearsExp: number;
  specialties: string[];
  bio: string;
};

const ATTORNEYS: Attorney[] = [
  {
    nameTh: "นางสาวอนุตรี",
    nameEn: "Miss Anutaree",
    license: "942/2554",
    licenseYear: "2011",
    image: credentials[0].img,
    role: "Notary Public & Senior Attorney",
    yearsExp: 14,
    specialties: ["Notarial Services Attorney", "Civil & Family Law", "Affidavit & Power of Attorney"],
    bio: "ผู้รับมอบอำนาจในการลงนามรับรองเอกสารจากสภาทนายความฯ ตั้งแต่ปี 2554 ดูแลงานรับรองลายมือชื่อ Affidavit, Power of Attorney และเอกสารระหว่างประเทศกว่า 30,000 ฉบับ",
  },
  {
    nameTh: "นายจิรพันธ์",
    nameEn: "Mr. Jirapan",
    license: "1112/2551",
    licenseYear: "2008",
    image: credentials[1].img,
    role: "Notary Public & Litigation Counsel",
    yearsExp: 17,
    specialties: ["Cross-border Litigation", "Commercial Contracts", "International Arbitration"],
    bio: "ทนายความที่ได้รับใบอนุญาตจากสภาทนายความตั้งแต่ปี 2551 เชี่ยวชาญคดีระหว่างประเทศและการรับรองเอกสารเพื่อใช้นอกราชอาณาจักร",
  },
  {
    nameTh: "นายจิรศักดิ์",
    nameEn: "Mr. Jirasak",
    license: "4759/2565",
    licenseYear: "2022",
    image: credentials[2].img,
    role: "Notary Public & Corporate Counsel",
    yearsExp: 12,
    specialties: ["Corporate M&A", "BOI Application", "FDI Structuring"],
    bio: "ที่ปรึกษากฎหมายธุรกิจที่ดูแลการจัดตั้ง BOI, การลงทุนต่างชาติ และการรับรองมติที่ประชุมผู้ถือหุ้นเพื่อใช้ในต่างประเทศ",
  },
  {
    nameTh: "นายปฏิภาณ",
    nameEn: "Mr. Patipan",
    license: "1114/2558",
    licenseYear: "2015",
    image: credentials[3].img,
    role: "Notary Public & Immigration Specialist",
    yearsExp: 11,
    specialties: ["Visa & Immigration", "MFA Legalization", "Embassy Coordination"],
    bio: "ทนายความสายตรวจคนเข้าเมือง ทำงานร่วมกับสถานทูตและกรมการกงสุลในการรับรองเอกสารและขอวีซ่าทุกประเภท",
  },
  {
    nameTh: "นายวราวุธ",
    nameEn: "Mr. Warawut",
    license: "3041/2565",
    licenseYear: "2022",
    image: credentials[4].img,
    role: "Notary Public & Real Estate Counsel",
    yearsExp: 9,
    specialties: ["Real Estate Conveyancing", "Land Office Procedures", "Lease Agreements"],
    bio: "ผู้เชี่ยวชาญด้านอสังหาริมทรัพย์และการรับรองสัญญาเช่า/ขายที่ดินเพื่อใช้กับสำนักงานที่ดินและธนาคารต่างประเทศ",
  },
  {
    nameTh: "นายวิวัฒน์",
    nameEn: "Mr. Wiwat",
    license: "931/2551",
    licenseYear: "2008",
    image: credentials[5].img,
    role: "Notary Public & Managing Partner",
    yearsExp: 17,
    specialties: ["Firm Management", "Notarial Services", "Translation Certification"],
    bio: "หุ้นส่วนผู้บริหารและทนาย Notary Public ของบริษัท ดูแลคุณภาพการรับรองเอกสารและการแปลที่ผ่านสภาทนายความฯ ทุกฉบับ",
  },
];

export const Route = createFileRoute("/about/team/")({
  head: () => {
    const title = "ทีมทนายความและ Notary Public — NYC Language Institute";
    const desc = "พบกับทีมทนายความ Notary Public 6 ท่านที่ได้รับใบอนุญาตจากสภาทนายความแห่งประเทศไทย เชี่ยวชาญงานรับรองเอกสารระหว่างประเทศ Visa และกฎหมายธุรกิจ — เริ่มต้นง่าย ๆ แค่ทักไลน์ เราดูแลจนจบทุกขั้นตอน";
    return {
      meta: [
        { title: ctaTitle(title, "/about/team", "th") },
        { name: "description", content: ctaDesc(desc, "/about/team", "th")},
        { property: "og:title", content: ctaTitle(title, "/about/team", "th")},
        { property: "og:description", content: ctaDesc(desc, "/about/team", "th")},
        { property: "og:url", content: URL },
        { property: "og:type", content: "profile" },
      ],
      links: canonicalAndHreflang("/about/team"),
      scripts: [consolidateGraph([
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["AboutPage", "ProfilePage"],
            "@id": `${URL}#webpage`,
            url: URL,
            name: title,
            description: desc,
            inLanguage: "th-TH",
            isPartOf: { "@id": `${SITE}#website` },
            datePublished: "2024-01-01",
            dateModified: SITE_CONTENT_REVIEWED,
            mainEntity: { "@id": `${URL}#org` },
            about: ATTORNEYS.map((a) => ({ "@id": `${URL}#${a.license.replace("/", "-")}` })),
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: [".aeo-speakable", "h1", "h2"],
            },
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["LegalService", "Organization"],
            "@id": `${URL}#org`,
            name: "NYC Legal & Notary Service Co., Ltd.",
            url: SITE,
            address: NYC_POSTAL_ADDRESS,
            sameAs: [`${SITE}#org`],
            employee: ATTORNEYS.map((a) => ({
              "@type": "Person",
              "@id": `${URL}#${a.license.replace("/", "-")}`,
              mainEntityOfPage: { "@id": `${URL}#webpage` },
              name: `${a.nameTh} (${a.nameEn})`,
              givenName: a.nameEn,
              jobTitle: a.role,
              worksFor: { "@id": `${SITE}#org` },
              image: `${SITE}${a.image}`,
              description: a.bio,
              knowsAbout: a.specialties,
              knowsLanguage: ["th", "en"],
              award: `${a.yearsExp}+ years legal practice in Thailand`,
              memberOf: {
                "@type": "Organization",
                name: "Lawyers Council of Thailand",
                url: "https://www.lawyerscouncil.or.th/2019/",
              },
              hasOccupation: {
                "@type": "Occupation",
                name: a.role,
                occupationLocation: { "@type": "Country", name: "Thailand" },
                skills: a.specialties.join(", "),
              },
              hasCredential: {
                "@type": "EducationalOccupationalCredential",
                credentialCategory: "license",
                name: `Notary Public License No. ${a.license}`,
                recognizedBy: { "@type": "Organization", name: "Lawyers Council of Thailand" },
                dateCreated: a.licenseYear,
              },
            })),
          }),
        },
        {
          type: "application/ld+json" as const,
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "About", item: `${SITE}/about/team` },
              { "@type": "ListItem", position: 3, name: "Team", item: URL },
            ],
          }),
        },
      ])],
    };
  },
  component: TeamPage,
});

function TeamPage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      <section className="border-b border-white/10 px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-5xl space-y-5">
          <div className="text-xs font-bold uppercase tracking-widest text-gold">About · Our Team</div>
          <h1 className="aeo-speakable text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            ทีมทนาย Notary Public 6 ท่าน — ใบอนุญาตจากสภาทนายความแห่งประเทศไทย
          </h1>
          <p className="aeo-speakable max-w-3xl text-base text-white/80 sm:text-lg">
            ทุกการรับรองเอกสาร NYC Legal & Notary Service ดำเนินการโดยทนายความที่ได้รับ
            <strong className="text-gold"> ใบอนุญาตประกอบวิชาชีพและใบอนุญาตเป็นผู้ทำคำรับรองลายมือชื่อและเอกสาร </strong>
            จากสภาทนายความฯ โดยตรง — ตรวจสอบรายชื่อและเลขใบอนุญาตได้กับสภาทนายความ
          </p>
          <p className="text-xs text-white/80">
            อัพเดทล่าสุด: <time dateTime={SITE_CONTENT_REVIEWED}>{SITE_CONTENT_REVIEWED}</time> · ตรวจสอบโดยฝ่ายกำกับการปฏิบัติงานของบริษัท
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
        <ul className="grid gap-6 sm:grid-cols-2">
          {ATTORNEYS.map((a) => (
            <li
              key={a.license}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-gold/40"
            >
              <div className="flex items-start gap-4">
                <img
                  src={a.image}
                  alt={`${a.nameTh} (${a.nameEn}) — Notary Public License ${a.license}`}
                  width={96}
                  height={96}
                  loading="lazy"
                  decoding="async"
                  className="h-20 w-20 shrink-0 rounded-xl border border-gold/30 object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">{a.role}</div>
                  <div className="mt-0.5 text-lg font-bold text-white">{a.nameTh}</div>
                  <div className="text-sm text-white/70">{a.nameEn}</div>
                  <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-wider">
                    <span className="rounded-full bg-emerald-600/15 px-2 py-0.5 text-emerald-300">
                      License {a.license}
                    </span>
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-white/75">
                      {a.yearsExp}+ ปีประสบการณ์
                    </span>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-white/80">{a.bio}</p>
              <div className="mt-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">ความเชี่ยวชาญ</div>
                <ul className="mt-1 flex flex-wrap gap-1.5">
                  {a.specialties.map((sp) => (
                    <li
                      key={sp}
                      className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[11px] text-white/80"
                    >
                      {sp}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4">
                <Link
                  to="/about/team/$slug"
                  params={{ slug: LICENSE_TO_SLUG[a.license] }}
                  className="inline-block text-sm font-semibold text-gold hover:underline"
                >
                  อ่านประวัติเต็มและขอนัดหมาย →
                </Link>
              </div>
            </li>
          ))}

        </ul>

        <section className="mt-12 rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/10 via-white/[0.04] to-transparent p-6 sm:p-8">
          <h2 className="text-xl font-extrabold sm:text-2xl">ตรวจสอบใบอนุญาตได้ที่ไหน</h2>
          <p className="mt-2 text-sm text-white/80">
            ทุกใบอนุญาตที่ระบุข้างต้นสามารถตรวจสอบกับ
            <a
              href="https://www.lawyerscouncil.or.th/2019/"
              target="_blank"
              rel="noopener"
              className="ml-1 text-gold underline-offset-4 hover:underline"
            >
              สภาทนายความในพระบรมราชูปถัมภ์
            </a>
            ได้โดยตรง บริษัทยินดีแสดงเอกสารต้นฉบับให้ลูกค้าก่อนการลงนามรับรองทุกครั้ง
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/trust"
              className="rounded-full bg-gold px-5 py-2 text-sm font-bold text-navy-deep hover:bg-gold/90"
            >
              ดูเอกสารความน่าเชื่อถือทั้งหมด
            </Link>
            <Link
              to="/quote"
              className="rounded-full border border-white/20 px-5 py-2 text-sm font-bold text-white hover:bg-white/10"
            >
              ขอใบเสนอราคา
            </Link>
          </div>
        </section>
      <RelatedHubGrid locale="th" />
      </div>
    </main>
  );
}
